
import { useCallback, useEffect, useRef, useState } from "react";
import { db } from "../../firebaseClient";
import { computeDashboardSummary, TodoItem } from "../services/todosSummary";
import type { DailySummaryResult } from "../services/dailySummaryService";

export type LiveStatus = "connecting" | "connected" | "offline" | "error" | "paused";

export function useTodosLive() {
  const [liveStatus, setLiveStatus] = useState<LiveStatus>("connecting");
  const [liveMsg, setLiveMsg] = useState<string>("監控連線中斷，嘗試重連...");
  const [data, setData] = useState<DailySummaryResult | null>(null);
  
  // Debug Props
  const [rawDocs, setRawDocs] = useState<TodoItem[]>([]);
  const [lastSnapshotAt, setLastSnapshotAt] = useState<Date | null>(null);
  const [snapshotMeta, setSnapshotMeta] = useState<any | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const unsubRef = useRef<null | (() => void)>(null);
  const retryTimerRef = useRef<number | null>(null);
  const reconnectBusyRef = useRef<boolean>(false);

  // 1. Cleanup Function
  const cleanupTodosLive = useCallback(() => {
    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (unsubRef.current) {
      try { unsubRef.current(); } catch (e) { console.warn("Unsub error", e); }
      unsubRef.current = null;
    }
  }, []);

  // 2. Server Probe (Verify actual connectivity)
  const probeTodosServer = useCallback(async () => {
    // Compat SDK
    const todosCol = db.collection("todos"); 
    const q = todosCol.limit(1);
    await q.get({ source: 'server' }); 
  }, []);

  // 3. Schedule Retry (Exponential Backoff)
  const scheduleRetry = useCallback((reason?: string, startFn?: () => void) => {
    setRetryCount(prev => prev + 1);
    // Use local var for delay calc
    const attempt = retryCount + 1; 

    // 1s, 2s, 4s, 8s, 16s... cap at 30s
    const delayMs = Math.min(30000, 1000 * Math.pow(2, Math.min(attempt, 5)));
    
    setLiveStatus("error");
    setLiveMsg(reason 
      ? `監控連線中斷 (${reason})；${Math.round(delayMs / 1000)} 秒後重試...` 
      : `${Math.round(delayMs / 1000)} 秒後重試...`
    );

    if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
    
    retryTimerRef.current = window.setTimeout(() => {
      if (startFn) startFn();
    }, delayMs);
  }, [retryCount]);

  // 4. Start Live Listener
  const startTodosLive = useCallback(async () => {
    cleanupTodosLive();

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setLiveStatus("offline");
      setLiveMsg("目前網路離線，等待連線恢復後自動重連…");
      // Schedule retry just in case online event misses or flakes
      scheduleRetry("網路離線", () => startTodosLive());
      return;
    }

    setLiveStatus("connecting");
    setLiveMsg("建立監聽連線中...");

    // Probe first
    try {
      await probeTodosServer();
    } catch (e: any) {
      const msg = e?.code === "permission-denied" 
        ? "權限不足" 
        : e?.code ? `連線失敗（${e.code}）` : "連線失敗";
      
      console.warn("[TodosLive] Probe failed:", e);
      scheduleRetry(msg, () => startTodosLive());
      return;
    }

    // Establish Listener
    try {
        const todosCol = db.collection("todos");
        // Compat SDK supports onSnapshot directly on collection (query)
        const todosQ = todosCol; 

        unsubRef.current = todosQ.onSnapshot(
          { includeMetadataChanges: true },
          (snap: any) => {
            const fromCache = snap.metadata.fromCache;
            setLastSnapshotAt(new Date());
            setSnapshotMeta(snap.metadata);
    
            if (typeof navigator !== "undefined" && navigator.onLine === false) {
              setLiveStatus("offline");
              setLiveMsg("目前網路離線，等待連線恢復後自動重連…");
              return;
            }
    
            if (fromCache) {
              setLiveStatus("connecting");
              setLiveMsg("連線不穩（資料可能來自快取），持續嘗試連回伺服器…");
              if (!retryTimerRef.current) {
                  scheduleRetry("資料來自快取", () => startTodosLive());
              }
            } else {
              // Connected & Fresh
              setRetryCount(0);
              if (retryTimerRef.current) {
                window.clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
              }
              setLiveStatus("connected");
              setLiveMsg("連線正常 (即時同步中)");
              
              // Process Data
              const raw = snap.docs.map((d: any) => ({ id: d.id, ...d.data() } as any));
              setRawDocs(raw);

              // Filter for dashboard Logic (OPEN only)
              const openTodos = raw.filter((t: any) => t.todoStatus === 'OPEN');
              const result = computeDashboardSummary(openTodos);
              setData(result);
            }
          },
          (err: any) => {
            console.warn("[TodosLive] Snapshot error:", err);
            const msg = err?.code ? `onSnapshot error: ${err.code}` : "onSnapshot error";
            scheduleRetry(msg, () => startTodosLive());
          }
        );
    } catch (err: any) {
        console.error("[TodosLive] Setup error:", err);
        scheduleRetry(err.message, () => startTodosLive());
    }
  }, [cleanupTodosLive, probeTodosServer, scheduleRetry]);

  // 5. Manual Reconnect Handler
  const handleManualReconnect = useCallback(async () => {
    if (reconnectBusyRef.current) return;
    reconnectBusyRef.current = true;

    try {
      setLiveStatus("connecting");
      setLiveMsg("手動重新連線中…");
      setRetryCount(0);
      
      cleanupTodosLive();

      // Force network toggle
      try {
        await db.disableNetwork();
        await new Promise(r => setTimeout(r, 500));
        await db.enableNetwork();
      } catch (e) {
        console.log("[TodosLive] network toggle skipped or failed", e);
      }

      await startTodosLive();
    } finally {
      reconnectBusyRef.current = false;
    }
  }, [cleanupTodosLive, startTodosLive]);

  // 6. Stop Listener manually
  const stopListener = useCallback(() => {
      cleanupTodosLive();
      setLiveStatus("paused");
      setLiveMsg("監聽已手動停止");
  }, [cleanupTodosLive]);

  // 7. Lifecycle
  useEffect(() => {
    void startTodosLive();

    const onOnline = () => {
      console.log("[TodosLive] Network online -> Reconnecting");
      void handleManualReconnect();
    };
    
    window.addEventListener("online", onOnline);

    return () => {
      window.removeEventListener("online", onOnline);
      cleanupTodosLive();
    };
  }, [startTodosLive, handleManualReconnect, cleanupTodosLive]);

  return { 
    liveStatus, 
    liveMsg, 
    data, 
    rawDocs,
    lastSnapshotAt,
    snapshotMeta,
    retryCount,
    isActive: !!unsubRef.current,
    handleManualReconnect,
    startListener: startTodosLive,
    stopListener
  };
}
